import { useRef, useState } from "react";
import { GrFileUploadProgressListItemProps } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { getFileNameWithoutExt, markAnsSortInvalidFiles } from "@newComponents/common/dropSection/DropSectionProgressBar";
import { getDocsFileIconName } from "@newLibs/converting";
import { MvnMessage } from "@newLibs/utils";
import { enqueueSnackbar } from "@newHooks/context/snackbar/notification.context";
import { useCreatePresignedPostUrlBulkMutation } from "@newHooks/mutation/common/createPresignedPostUrlBulk.mutation";
import { useCreateDocumentFilesMutation } from "@newHooks/mutation/folderFile/createDocumentFiles.mutation";
import { FolderKey } from "@newHooks/query/folder/key";
import { InventoryKey } from "@newHooks/query/inventory/key";
import { BILLING_FUNCTIONALITY_CODE } from "@maven-msg/code/billing/functionality";
import { DOCS_DOCUMENT_CODE } from "@maven-msg/code/docs/document";
import { DOCS_FOLDER_CODE } from "@maven-msg/code/docs/folder";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs/document";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs/folder";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { CreatableDocumentFile, FolderFileCriteria, InvalidDocumentFile } from "@maven-rest/docs/FolderFile.dto";
import { RefResourceType, RefServiceType } from "@maven-rest/ref/Ref.dto";
import { convertFileTypeToS3ResourceContentType } from "@maven-rest/resource/S3.lib";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";
import { TypeHelper } from "@maven-helper/helpers/Type.helper";

export type FileUploadProgressItemWithFile = GrFileUploadProgressListItemProps & { file: File };

export const ARCHIVER_UPLOAD_CALLBACK_INVALID_QUERY_KEYS = [FolderKey.BrowseFolders, InventoryKey.InventoryBalance];
export const DEFAULT_DOCUMENT_FILE_UPLOAD_CALLBACK_INVALID_QUERY_KEYS = [FolderKey.BrowseFolders];

const FILE_UPLOAD_ALL_FAIL_CONDITION_BE_CODES = [
  BILLING_FUNCTIONALITY_CODE.BILLING_FUNCTIONALITY_USAGE_EXCEEDED_DOCS_STORAGE.code,
  BILLING_FUNCTIONALITY_CODE.BILLING_FUNCTIONALITY_NOT_SUBSCRIBED.code,
  DOCS_FOLDER_CODE.DOCS_FOLDER_FILES_COUNT_EXCEED.code,
  DOCS_FOLDER_CODE.DOCS_FOLDER_FILES_EMPTY_INPUT_LIST.code,
  DOCS_FOLDER_CODE.DOCS_FOLDER_FILES_BULK_CHILDREN_EXCEED.code,
  DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_FILE_RESOURCE_LENGTH_NOT_MATCH.code,
];

interface BulkFileUploadProps {
  organizationId: bigint;
  uploadSizeLimitInMB: number;
  uploadSizeLimitInMBForHostOS: number;
  folderType: FolderType;
  folderId?: bigint;
  isTableDndBulkUpload?: boolean;
  invalidQueryKeys?: Array<string>;
  onPresignedUploadSuccess?: () => void;
  onFolderFileCreateSuccess?: (res?: { success: number; failed: number }) => void;
  onFolderFileCreateFailed?: (failedCount: number) => void;
}

export function useBulkFileUpload(props: BulkFileUploadProps) {
  const {
    organizationId,
    uploadSizeLimitInMB,
    uploadSizeLimitInMBForHostOS,
    folderType,
    folderId,
    isTableDndBulkUpload,
    invalidQueryKeys,
    onPresignedUploadSuccess,
    onFolderFileCreateSuccess,
    onFolderFileCreateFailed,
  } = props;

  const queryClient = useQueryClient();

  const [fileUploadList, setFileUploadList] = useState<Array<FileUploadProgressItemWithFile>>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const creatableDocumentFileListRef = useRef<Array<CreatableDocumentFile>>([]);

  const changeStatusToFailedOnlyProcessingFile = () => {
    setFileUploadList((prev) => prev.map((p) => ({ ...p, status: p.status === "DONE" ? "DONE" : "ERROR" })));
  };

  const { mutate: createPresignedPostUrlBulk, isLoading: isCreatePresignedPostUrlBulkLoading } = useCreatePresignedPostUrlBulkMutation({
    onSuccess: async (data) => {
      const creatableDocumentFileList: Array<CreatableDocumentFile> = await (async () => {
        try {
          return await Promise.all(
            data.presignedUrls.map((presignedUrl, index) => {
              return new Promise<CreatableDocumentFile>((res, rej) => {
                const { method, url, key, bucket } = presignedUrl;
                const { file } = fileUploadList[index];
                const fileName = file.name.normalize("NFC");

                const creatableDocumentFileDto: CreatableDocumentFile = {
                  title: getFileNameWithoutExt(fileName),
                  version: FolderFileCriteria.FOLDER_FILE_DEFAULT_VERSION,
                  contentBucket: bucket,
                  contentKey: key,
                  contentName: fileName,
                };

                const xhr = new XMLHttpRequest();
                xhr.open(method, url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                xhr.upload.onprogress = ({ lengthComputable, loaded, total }) => {
                  if (lengthComputable) {
                    setFileUploadList((prev) =>
                      prev.map((item, idx) => (idx === index ? { ...item, progress: (loaded / total) * 100 } : item))
                    );
                  }
                };

                xhr.onloadend = () => {
                  const statusCode = xhr.status;
                  const isSuccessful = statusCode >= 200 && statusCode < 300;

                  setFileUploadList((prev) =>
                    prev.map((item, idx) => (idx === index ? { ...item, status: isSuccessful ? "DONE" : "ERROR" } : item))
                  );

                  isSuccessful ? res(creatableDocumentFileDto) : rej(new Error(`File: [${file.name}] upload has been failed`));
                };

                xhr.send(file);
              });
            })
          );
        } catch (e: any) {
          enqueueSnackbar({ message: `Create PresignedUrl has failed: ${e.message}`, severity: "warning" });
          return [];
        }
      })();

      if (!creatableDocumentFileList.length) return;

      if (isTableDndBulkUpload) {
        createDocumentFilesRequest({
          files: creatableDocumentFileList,
          folderId,
          organizationId,
          folderType,
          isInvalidFileSkippable: true,
        });
        return;
      }

      const previousCreatableFileList = creatableDocumentFileListRef.current;
      creatableDocumentFileListRef.current = [...creatableDocumentFileList, ...previousCreatableFileList];
      onPresignedUploadSuccess?.();
    },
    onError: (error) => {
      const onProcessingCount = fileUploadList.filter((file) => ["WAIT", "UPLOADING"].includes(file.status))?.length || 0;
      onFolderFileCreateFailed?.(onProcessingCount);
      changeStatusToFailedOnlyProcessingFile();

      enqueueSnackbar({
        message: MvnMessage(COMMON_LANG.ERROR, { message: error.responseMessage }),
        severity: "warning",
      });
    },
  });

  const { mutate: createDocumentFilesRequest, isLoading: isCreateDocumentFilesLoading } = useCreateDocumentFilesMutation({
    onSuccess: (data) => {
      const { entries, invalidFiles } = data;

      if (isTableDndBulkUpload) {
        const hasInvalidFile = invalidFiles.length;

        if (hasInvalidFile) {
          const invalidIndexArray = invalidFiles.map((invalidFile) => invalidFile.index);

          setFileUploadList((prev) =>
            prev.map((file, index) => (invalidIndexArray.includes(String(index)) ? { ...file, status: "ERROR" } : file))
          );

          enqueueSnackbar({
            message: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_UPLOAD_PARTIALLY_FAIL, { failCount: invalidFiles.length }),
            severity: "warning",
            autoHideDuration: 10000,
          });
        }

        if (!hasInvalidFile) {
          enqueueSnackbar({ message: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_ARCHIVER_CREATE_SUCC) });
        }
      }

      if (!isTableDndBulkUpload) {
        enqueueSnackbar({ message: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_ARCHIVER_CREATE_SUCC) });
      }

      invalidQueryKeys?.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      onFolderFileCreateSuccess?.({ success: entries.length, failed: invalidFiles.length });
    },
    onError: (error) => {
      const errCode = error.responseCode;
      const errData = error.response?.data?.binds;

      const errorFileIndex = errData?.errorFileIndex;
      const failedFileCnt = errData?.failedFileCnt || 0;
      const invalidFiles: Array<InvalidDocumentFile> = errData?.invalidFiles?.data;
      const isPartiallyFailed = Array.isArray(invalidFiles);
      const isAllFailed = FILE_UPLOAD_ALL_FAIL_CONDITION_BE_CODES.includes(errCode);

      if (!isAllFailed && !isTableDndBulkUpload) {
        const isValidErrorIndex = !TypeHelper.isNullish(errorFileIndex);

        if (isValidErrorIndex) {
          setFileUploadList((prev) =>
            prev.map((fileUpload, index) =>
              compareBigintSafe(index, errorFileIndex) ? { ...fileUpload, status: "ERROR", progress: 0 } : fileUpload
            )
          );
        }

        if (errCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_FILE_SIZE_EXCEED.code) {
          enqueueSnackbar({
            message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_EXCEEDS_MAX_SIZE, { limit: uploadSizeLimitInMBForHostOS }),
            severity: "warning",
          });
        }

        enqueueSnackbar({
          message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_FAIL, { message: error.responseMessage }),
          severity: "warning",
        });
        onFolderFileCreateFailed?.(1);
        return;
      }

      // NOTE: 전체 파일의 실패경우 처리.
      changeStatusToFailedOnlyProcessingFile();
      onFolderFileCreateFailed?.(isTableDndBulkUpload ? (isPartiallyFailed ? invalidFiles.length : parseInt(failedFileCnt)) : 1);
      enqueueSnackbar({
        message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_FAIL, { message: error.responseMessage }),
        severity: "warning",
      });
    },
  });

  const onBulkCreateFolderFile = (singleFileUploadInfo?: { title: string; version: string }) => {
    const currentFiles = creatableDocumentFileListRef.current;

    if (!!singleFileUploadInfo && currentFiles.length > 1) {
      enqueueSnackbar({ message: MvnMessage(COMMON_LANG.ERROR), severity: "danger" });
      return;
    }

    const files: Array<CreatableDocumentFile> = (() => {
      if (!!singleFileUploadInfo) {
        const { title, version } = singleFileUploadInfo;
        return [{ ...currentFiles[0], title: title.trim(), version: version.trim() }];
      }

      return currentFiles;
    })();

    createDocumentFilesRequest({
      files,
      folderId,
      organizationId,
      folderType,
    });
  };

  const handleChangeFileList = (fileList: Array<File>) => {
    const totalLength = fileList.length + fileUploadList.length;
    if (totalLength > FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT) {
      enqueueSnackbar({
        message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_MAX_COUNT_HELPER_TEXT, {
          count: FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT,
        }),
        severity: "danger",
      });
      return;
    }

    const validFileList: Array<FileUploadProgressItemWithFile> = markAnsSortInvalidFiles(fileList, {
      uploadSizeLimitInMB,
      uploadSizeLimitInMBForHostOS,
      folderType,
      isErrorNotify: true,
    }).map(({ file, invalidReason }) => ({
      file,
      name: getFileNameWithoutExt(file.name.normalize("NFC")),
      type: getDocsFileIconName(file.type),
      progress: 0,
      status: !invalidReason ? "UPLOADING" : "ERROR",
    }));

    // newly added files are must placed at first
    setFileUploadList([...validFileList, ...fileUploadList]);

    if (!validFileList.length) {
      return;
    }

    createPresignedPostUrlBulk({
      fileTypes: validFileList
        .filter((file) => file.status !== "ERROR")
        .map(({ file }) => ({
          name: file.name.normalize("NFC"),
          resourceType: RefResourceType.Docs_FolderFile,
          contentType: convertFileTypeToS3ResourceContentType(file.type.toLowerCase()),
        })),
      organizationId,
      folderType,
      service: RefServiceType.Docs,
    });
  };

  const handleAttachmentRowDelete = (index: number) => {
    if (index < 0 || index > fileUploadList.length) return;

    setFileUploadList([...fileUploadList.filter((_, idx) => idx !== index)]);
    creatableDocumentFileListRef.current = creatableDocumentFileListRef.current.filter((_, idx) => idx !== index);
  };

  return {
    fileInputRef,
    fileUploadList,
    isCreatePresignedPostUrlBulkLoading,
    isCreateDocumentFilesLoading,
    creatableFileLength: creatableDocumentFileListRef.current.length,
    setFileUploadList,
    handleChangeFileList,
    handleAttachmentRowDelete,
    createPresignedPostUrlBulk,
    createDocumentFilesRequest: onBulkCreateFolderFile,
  };
}
